import { converToStorageUnit } from "../utils";

let domain: string;
let hostApi: string;
let hostS3: string;

const hostname = window.location.hostname.split(".")[0];

// Check if hostname is an IP address (for network device testing)
const isIPAddress = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(
  window.location.hostname,
);

// In cloud deploys the frontend and backend live on separate hosts, and the build
// injects the backend URL into REACT_APP_DOMAIN (via __DOMAIN_PLACEHOLDER__). When
// it's a real URL, use it directly — the hostname switch below only knows the legacy
// localhost / develop / www hosts and would otherwise fall back to localhost,
// breaking every API call (including CSRF and login) on any other domain.
const envDomain = process.env.REACT_APP_DOMAIN;
const hasEnvDomain = !!envDomain && envDomain.indexOf("http") === 0;

if (hasEnvDomain) {
  const base = (envDomain as string).replace(/\/+$/, "");
  domain = base;
  hostApi = `${base}/api/v1/`;
  hostS3 = "https://s3.us-east-2.amazonaws.com/";
} else if (
  window.location.hostname.includes("-frontend-pr-") &&
  window.location.hostname.endsWith(".onrender.com")
) {
  // Render PR preview: our host is cherryclipz-v2-frontend-pr-<N>.onrender.com and the
  // sibling backend is cherryclipz-v2-backend-pr-<N>.onrender.com. Derive it from our own
  // hostname because Render does NOT copy REACT_APP_DOMAIN (a sync:false secret) into
  // preview environments, so envDomain is blank here — without this we'd fall through to
  // the localhost default below and every API call fails with "Network Error".
  const backendHost = window.location.hostname.replace("-frontend-pr-", "-backend-pr-");
  domain = `https://${backendHost}`;
  hostApi = `https://${backendHost}/api/v1/`;
  hostS3 = "https://s3.us-east-2.amazonaws.com/";
} else {
switch (hostname) {
  case "localhost":
    domain = "http://localhost:8000/";
    hostApi = "http://localhost:8000/api/v1/";
    hostS3 = "http://localhost:4566/";
    break;
  case "develop":
    domain = "https://develop.cherryclipz.com";
    hostApi = "https://develop.cherryclipz.com/api/v1/";
    hostS3 = "https://s3.us-east-2.amazonaws.com/";
    break;
  case "www":
    domain = "https://www.cherryclipz.com";
    hostApi = "https://www.cherryclipz.com/api/v1/";
    hostS3 = "https://s3.us-east-2.amazonaws.com";
    break;
  default:
    // Handle network IP addresses (for device testing) and unknown hostnames
    if (isIPAddress) {
      // Use the network IP for backend, but keep localhost for localstack
      domain = `http://${window.location.hostname}:8000/`;
      hostApi = `http://${window.location.hostname}:8000/api/v1/`;
      hostS3 = `http://${window.location.hostname}:4566/`;
    } else {
      // Fallback to localhost for unknown hostnames
      domain = "http://localhost:8000/";
      hostApi = "http://localhost:8000/api/v1/";
      hostS3 = "http://localhost:4566/";
    }
    break;
}
}

export { domain, hostApi, hostS3 };

export const authUrl = {
  get_email: `${hostApi}get-email/`,
  get_pass: `${hostApi}get-code/`,
  signUp: `${hostApi}sign-up/`,
  google: `${hostApi}auth/google/`,
};
export const getUserUrl = `${hostApi}get-user/`;

export const logoutUrl = `${hostApi}logout/`;
export const csrfUrl = `${hostApi}csrf/`;
// Which build is answering (git commit, branch, the newest Android build). Public; see
// emo/emo/version.py. The phone app compares its own build number with `android.code`.
export const versionUrl = `${hostApi}version/`;
// Both stores require an in-app way to delete an account (Apple 5.1.1(v), Google's User Data
// policy). It really deletes — see emo_users/account_deletion.py.
export const deleteAccountUrl = `${hostApi}delete-account/`;
export const getUserVideosUrl = `${hostApi}user-downloaded-videos/`;
export const uploadUrl = hostS3;
export const getSentencesUrl = `${hostApi}user-create/`;
export const getSentencesStreamUrl = `${hostApi}user-create-stream/`;
// Lightweight {clipId: posterUrl} companion to the stream — the ET polls this while
// background thumbnails are still generating and patches posters in without a reload.
export const clipThumbnailsUrl = `${hostApi}clip-thumbnails/`;
// Phone → Premiere live ticking: the phone POSTs its pick-set to `set/`; the desktop plugin
// arms via `arm/` and polls `${livePicksGetUrl}${transcriptId}/`. See the couch-editing spec.
export const livePicksArmUrl = `${hostApi}live-picks/arm/`;
export const livePicksSetUrl = `${hostApi}live-picks/set/`;
export const livePicksGetUrl = `${hostApi}live-picks/`;
// Live system-load signal for the in-app health banner (queue depth + DB pressure + status).
export const systemHealthUrl = `${hostApi}system-health/`;
export const makeVideoUrl = `${hostApi}make-video/`;
export const autoClipUrl = `${hostApi}auto-clip/`;
// CherryClipz Auto: the make.com webhook a finished export is pushed to.
export const autoDestinationUrl = `${hostApi}auto-destination/`;
export const autoDestinationTestUrl = `${hostApi}auto-destination/test/`;
export const exportStyleUrl = `${hostApi}export-style/`;
// Your logo (My Style): upload → a clean PNG under your own prefix.
export const brandLogoUrl = `${hostApi}brand-logo/`;
// The 📤 Post button on an Exported row: send that video to the Auto webhook now.
export const autoPostUrl = (creationId: number) => `${hostApi}auto-post/${creationId}/`;
// Posting calendar (My Library → Calendar)
export const calendarUrl = `${hostApi}calendar/`;
export const calendarSettingsUrl = `${hostApi}calendar/settings/`;
export const calendarFillUrl = `${hostApi}calendar/fill/`;
export const calendarSkipUrl = (postId: number) => `${hostApi}calendar/${postId}/skip/`;
export const calendarClearUrl = `${hostApi}calendar/clear/`;
// Curated clip shortlist for the Reel page — a query, no rendering.
export const clipShortlistUrl = (transcriptId: number | string) =>
  `${hostApi}clip-shortlist/${transcriptId}/`;
export const regenerateThumbnailsUrl = `${hostApi}regenerate-thumbnails/`;
// Shareable ET
export const createShareUrl = `${hostApi}share/create/`;
export const listSharesUrl = `${hostApi}share/list/`;
export const revokeShareUrl = `${hostApi}share/revoke/`;
export const shareMakeVideoUrl = `${hostApi}share/make-video/`;
export const creationStatusSharedUrl = `${hostApi}share/creation-status/`;
export const creationDownloadSharedUrl = `${hostApi}share/creation-download/`;
export const aiProxySharedUrl = `${hostApi}share/ai-proxy/`;
export const resolveShareUrl = (token: string) => `${hostApi}s/${token}/resolve/`;
export const staffPassUrl = (token: string) => `${hostApi}s/${token}/staff-pass/`;
export const shareSrtUrl = `${hostApi}share/srt/`;
// CherryClipz Notetaker (meeting bots)
export const meetingsUrl = `${hostApi}meetings/`;
export const meetingCancelUrl = `${hostApi}meetings/cancel/`;
export const meetingFeedUrl = `${hostApi}meetings/feed/`;
// A link can open the ET in a view: ?view=clips|categories|super (the notetaker's email does).
export const urlViews: Record<string, string> = {
  clips: "Create a Video",
  categories: "AIB",
  super: "Super Transcript",
};
export const getMyCreationsUrl = `${hostApi}my-creations/`;
export const stitchCreationsUrl = `${hostApi}stitch-creations/`;
export const deleteUrl = `${hostApi}delete-downloaded-video/`;
export const deleteTranscriptUrl = `${hostApi}delete-transcript/`;
export const renameTranscriptUrl = `${hostApi}rename-transcript/`;
export const deleteMyCreationUrl = `${hostApi}delete-my-creation/`;
export const cancelMyCreationUrl = `${hostApi}cancel-my-creation/`;
export const updateMyCreationDescriptionUrl = `${hostApi}update-my-creation-description/`;
export const host = "http://localhost/";
export const getPagesUrl = `${hostApi}get-pages/`;
export const presignedUrlEndpoint = `${hostApi}generate-presigned-url/`;
export const updateUploadVideoData = `${hostApi}update-upload-video-data/`;
export const importFromUrlEndpoint = `${hostApi}import-from-url/`;
export const getUserTranscriptVideosUrl = `${hostApi}get-user-transcript-videos/`;
export const uploadStatusSseUrl = (downloadId: number) => `${hostApi}upload-status-sse/${downloadId}/`;
export const clipDirectDownloadUrl = `${hostApi}clip-direct-download/`;
export const edlExportUrl = `${hostApi}edl-export/`;
export const runWhisperUrl = `${hostApi}run-whisper/`;
export const getUserDownloadedVideosUrl = `${hostApi}user-downloaded-videos/`;

// Stripe URLs
export const createCheckoutSessionUrl = `${hostApi}create-checkout-session/`;
export const verifyPaymentUrl = `${hostApi}verify-payment/`;
export const createPortalSessionUrl = `${hostApi}create-portal-session/`;
export const subscriptionUrl = `${hostApi}subscription/`;
export const subscriptionStatusUrl = `${hostApi}subscription-status/`;
export const getPlanByNameUrl = `${hostApi}get-plan-by-name/`;
export const cancelSubscriptionUrl = `${hostApi}cancel-subscription/`;
export const getUserResourceUsageUrl = `${hostApi}get-user-resource-usage/`;
export const getUsageLimitsUrl = `${hostApi}usage-limits/`;
export const updateUserProfileUrl = `${hostApi}update-user-profile/`;
// Premiere panel download: ask (with the user's token) for a short-lived signed URL,
// then point the browser at it. The .zxp is never a plain public file.
export const pluginDownloadLinkUrl = `${hostApi}plugin/download-link/`;
export const EMO_NAME = "Emo Transcripts";
// Support inbox. Verified sending as "CherryClipz" via SendGrid (em68.cherryclipz.com) —
// mail on this address is independent of where the cherryclipz.com website points.
export const EMO_EMAIL = "Info@cherryclipz.com";
export const EMO_DOMAIN = "www.cherryclipz.com";

// Set REACT_APP_WHISPER_ENABLED=true in .env to show Whisper buttons.
// Leave unset (or false) in production — Whisper runs locally only.
export const WHISPER_ENABLED = process.env.REACT_APP_WHISPER_ENABLED === "true";
// Limits

// should be more then enough for the user a 2 hours video has +/- 4000 lines
export const maxLines = 99999;

export const APP_BAR_HEIGHT = 64;
export const SIZE_IN_BYTES = 3.5 * 1024 * 1024 * 1024 - 1;
// check settings in django this must be sync with the frontend
export const MAX_VIDEO_SIZE_FRENDLY = converToStorageUnit(SIZE_IN_BYTES);
export const MAX_VIDEO_SIZE_BYTES = SIZE_IN_BYTES;
export const MAX_VIDEO_DURATION = 3600; // 1 hour
export const GIF_MAX_DURATION = 30000; // 30 seconds
export const DRAG_TOOLBAR_HEIGHT = 64;
export const TIMELINE_HEIGHT = 180;
export const TIMELINE_TITLE_HEIGHT = Math.floor(TIMELINE_HEIGHT * 0.24); // 24% of timeline height
export const SENTIMENTS = { positive: false, negative: false, neutral: false };
export const SPACE = " ";
export const CREATE_VIDEO = "Create a Video";
export const CHART = "Charts";
export const CHERRY_PICK = "Cherry Pick";
export const SUPPORTED_VIDEO_FORMATS = [
  "mp4",
  "webm",
  "mts",
  "m2ts",
  "ts",
  "mov",
  "m4v",
  "mxf",
  // audio-only formats — transcribed into an ET (no thumbnails, no video)
  "mp3",
  "m4a",
  "wav",
  "aac",
  "flac",
  "ogg",
  "aiff",
];

// messages only needs the mp4, we don't need to send the ext to tasks
// the file is created base on the original format, so all tasks need to
// know is that a video should be created task type mp4
export const optionToKind = {
  "Create a MP4 video": "mp4",
  "Create a WEBM video": "mp4",
  "Create a MTS video": "mp4",
  "Create a M2TS video": "mp4",
  "Create a TS video": "mp4",
  "Create a MOV video": "mp4",
  "Create a M4V video": "mp4",
  "Create a MXF video": "mp4",
  // Audio-only sources: "Create a <AUDIO> video" still makes an MP4 — the backend
  // builds an audiogram (black background + burned captions + the audio).
  "Create a MP3 video": "mp4",
  "Create a M4A video": "mp4",
  "Create a WAV video": "mp4",
  "Create a AAC video": "mp4",
  "Create a FLAC video": "mp4",
  "Create a OGG video": "mp4",
  "Create a OGA video": "mp4",
  "Create a AIFF video": "mp4",
  "Create a AIF video": "mp4",
  "Create a WMA video": "mp4",
  "Create a MP3 audio": "mp3",
  // Video sources: export just the audio over a black + cherry-logo canvas (an MP4).
  "Create an Audio + Logo video": "audiogram",
  "Super Cut (searched words only)": "mp4_super_cut",
  "Create a GIF": "gif",
  "Create a SRT file": "srt",
  "Create a VTT file": "vtt",
  "Create zip file with all SRT's": "zip_srt",
};

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const VIEW_DRAGABLE_BUTTONS_AIB = {
  handle: true,
  arrowUp: true,
  arrowDown: true,
  arrowLeft: true,
  arrowRight: true,
  stats: true,
  length: false,
  seconds: false,
  search: true,
  subtitles: true,
  resetFilters: true,
  createVideo: true,
  views: true,
  playClip: true,
  precision: true,
  videoWidth: true,
};

export const VIEW_DRAGABLE_BUTTONS_AUTO_HIGHLIGHT = {
  handle: true,
  arrowUp: true,
  arrowDown: true,
  arrowLeft: true,
  arrowRight: true,
  stats: true,
  length: false,
  seconds: false,
  search: true,
  subtitles: true,
  resetFilters: true,
  createVideo: true,
  views: true,
  playClip: true,
  precision: true,
  videoWidth: true,
};

export const VIEW_DRAGABLE_BUTTONS_CATEGORIES = {
  handle: true,
  arrowUp: true,
  arrowDown: true,
  arrowLeft: true,
  arrowRight: true,
  stats: true,
  length: false,
  seconds: false,
  search: true,
  subtitles: true,
  resetFilters: true,
  createVideo: true,
  views: true,
  playClip: true,
  precision: true,
  videoWidth: true,
};

export const VIEW_DRAGABLE_BUTTONS_CONTENT_MODERATION = {
  handle: true,
  arrowUp: true,
  arrowDown: true,
  arrowLeft: true,
  arrowRight: true,
  stats: true,
  length: false,
  seconds: false,
  search: true,
  subtitles: false,
  spaces: true,
  resetFilters: true,
  createVideo: true,
  views: true,
  playClip: true,
  precision: true,
  videoWidth: true,
};
export const VIEW_DRAGABLE_BUTTONS_ENTITIES = {
  handle: true,
  arrowUp: true,
  arrowDown: true,
  arrowLeft: true,
  arrowRight: true,
  stats: true,
  length: false,
  seconds: false,
  resetFilters: true,
  search: true,
  createVideo: true,
  views: true,
  playClip: true,
  precision: true,
  videoWidth: true,
};

export const VIEW_DRAGABLE_BUTTONS_CHERRY_PICK = {
  handle: true,
  arrowUp: true,
  arrowDown: true,
  arrowLeft: true,
  arrowRight: true,
  stats: true,
  sentiment: true,
  speaker: true,
  length: true,
  seconds: true,
  spaces: true,
  subtitles: true,
  resetFilters: true,
  search: true,
  createVideo: true,
  views: true,
  playClip: true,
  precision: false,
  videoWidth: false,
  regroup: true,
};

export const VIEW_DRAGABLE_BUTTONS_CREATE_VIDEO = {
  handle: true,
  arrowUp: true,
  arrowDown: true,
  arrowLeft: true,
  arrowRight: true,
  stats: true,
  sentiment: true,
  speaker: true,
  spaces: true,
  length: true,
  seconds: true,
  resetFilters: true,
  search: true,
  createVideo: true,
  views: true,
  playClip: true,
  precision: true,
  subtitles: false,
  videoWidth: true,
  regroup: true,
};

export const VIEW_DRAGABLE_BUTTONS_WORDS = {
  handle: true,
  arrowUp: true,
  arrowDown: true,
  arrowLeft: true,
  arrowRight: true,
  stats: true,
  sentiment: false,
  speaker: false,
  length: false,
  seconds: false,
  subtitles: false,
  resetFilters: true,
  search: true,
  createVideo: true,
  views: true,
  playClip: true,
  precision: true,
  videoWidth: true,
};

export const VIEW_DRAGABLE_BUTTONS_CHART = {
  handle: true,
  arrowUp: true,
  arrowDown: true,
  arrowLeft: true,
  arrowRight: true,
  stats: true,
  sentiment: true,
  speaker: true,
  length: true,
  seconds: true,
  spaces: true,
  subtitles: false,
  resetFilters: true,
  search: true,
  createVideo: false,
  views: true,
  playClip: true,
  precision: false,
  videoWidth: false,
};

export const viewItems = [
  "Entities",
  "Auto Highlight",
  "AIB",
  "Cherry Pick",
  "Cherry Classic",
  "Create a Video",
  "Content Moderation",
  "Charts",
  "Super Transcript",
  "Mosaic",
  "Chessboard",
  "Cube",
];

export const mapViewToModel = {
  AIB: "aib",
  "Auto Highlight": "autohighlight",
  "Cherry Pick": "sentence",
  "Cherry Classic": "sentence",
  "Create a Video": "sentence",
  "Content Moderation": "contentmoderation",
  Entities: "entities",
  Charts: "sentence",
  Words: "word",
  "Super Transcript": "sentence",
  Mosaic: "sentence",
  Chessboard: "sentence",
  Cube: "sentence",
};

export const mapViewToUser = {
  Entities: "Entities",
  "Auto Highlight": "Highlights",
  AIB: "Categories",
  "Cherry Pick": "Cherry Pick",
  "Cherry Classic": "Cherry Classic",
  "Create a Video": "EMO Trasncript",
  "Content Moderation": "Content Moderation",
  Charts: "Charts",
  "Super Transcript": "Super Transcript",
  Mosaic: "Mosaic",
  Chessboard: "Chessboard",
  Cube: "Cube ✦",
};

// Owner decided in the last moment to change the name of the views to the user
export const translate = (view: string) => {
  return (mapViewToUser as Record<string, string>)[view] || view;
};

export const VIEW_DRAGABLE_BUTTONS_SUPER_TRANSCRIPT = {
  handle: true,
  arrowUp: true,
  arrowDown: true,
  arrowLeft: true,
  arrowRight: true,
  stats: true,
  sentiment: true,
  speaker: true,
  length: true,
  seconds: true,
  spaces: true,
  subtitles: false,
  resetFilters: true,
  search: true,
  createVideo: true,
  views: true,
  playClip: false,
  precision: false,
  videoWidth: false,
};

export const VIEW_DRAGABLE_BUTTONS_MOSAIC = {
  handle: false,
  arrowUp: false,
  arrowDown: false,
  arrowLeft: false,
  arrowRight: false,
  stats: true,
  sentiment: true,
  speaker: true,
  length: false,
  seconds: false,
  spaces: false,
  subtitles: false,
  resetFilters: true,
  search: true,
  createVideo: false,
  views: true,
  playClip: false,
  precision: false,
  videoWidth: false,
};

export const CURRENT_VIEW_CONFIG = {
  AIB: VIEW_DRAGABLE_BUTTONS_AIB,
  "Auto Highlight": VIEW_DRAGABLE_BUTTONS_AUTO_HIGHLIGHT,
  Categories: VIEW_DRAGABLE_BUTTONS_CATEGORIES,
  "Cherry Pick": VIEW_DRAGABLE_BUTTONS_CHERRY_PICK,
  "Cherry Classic": VIEW_DRAGABLE_BUTTONS_CHERRY_PICK,
  "Create a Video": VIEW_DRAGABLE_BUTTONS_CREATE_VIDEO,
  "Content Moderation": VIEW_DRAGABLE_BUTTONS_CONTENT_MODERATION,
  Entities: VIEW_DRAGABLE_BUTTONS_ENTITIES,
  Words: VIEW_DRAGABLE_BUTTONS_WORDS,
  Charts: VIEW_DRAGABLE_BUTTONS_CHART,
  "Super Transcript": VIEW_DRAGABLE_BUTTONS_SUPER_TRANSCRIPT,
  Mosaic: VIEW_DRAGABLE_BUTTONS_MOSAIC,
  Chessboard: VIEW_DRAGABLE_BUTTONS_MOSAIC,
  "Cube ✦": VIEW_DRAGABLE_BUTTONS_MOSAIC,
  Cube: VIEW_DRAGABLE_BUTTONS_MOSAIC,
};
